import * as React from 'react';
import cn from 'classnames';

import styles from './PageBody.module.scss';

type PageBodyProps = {
  /**
   * Optional extra class. Use for page-specific layout (e.g. grid columns)
   * — not for overriding width / margins, that's what `narrow` is for.
   */
  className?: string;
  /**
   * Clamp the body to a comfortable reading width and centre it. Use for
   * form-heavy pages (e.g. admin settings tabs) where stretching to the
   * full viewport hurts readability. Defaults to `false` so new pages
   * automatically match the full-width Assets / Collections / Shares
   * convention.
   */
  narrow?: boolean;
};

/**
 * Standard page-body wrapper. Lives inside `AppShell`'s content pane and
 * supplies the canonical `1.25rem` padding all dashboard pages use.
 *
 * Render this directly under `PageTitle` (or whatever page-level chrome
 * you have, like `SettingsNavigation` + `SaveChangesBar` on settings
 * tabs). The page-level chrome should remain full-width regardless of
 * the `narrow` prop, because consistency with sibling pages happens at
 * the page level — `narrow` only constrains the body content.
 */
export const PageBody: React.FC<PageBodyProps> = ({
  children,
  className,
  narrow,
}) => (
  <div
    className={cn(styles.PageBody, narrow && styles.narrow, className)}
    data-test="page-body"
  >
    {children}
  </div>
);

export default PageBody;
